import { useEffect, useRef, useState } from "react";
import Button from "../../../../components/fundamentals/button";
import RefreshIcon from "../../../../components/fundamentals/icons/refresh-icon";
import InputField from "../../../../components/form/input";
import useNotification from "../../../../hooks/use-notification";
import useImperativeDialog from "../../../../hooks/use-imperative-dialog";
import syncService, { SyncScope } from "../../../../services/sync.service";

/**
 * RAG-32 — Panneau de lancement des synchronisations PIM manuelles.
 *
 * Boutons : synchro complète, par PIM (Socoda / Interactiv), par fournisseur.
 * Chaque lancement demande une confirmation, désactive les boutons pendant
 * l'exécution, puis suit l'état du process (en attente -> en cours -> réussie / échouée).
 */

const STATUS_LABELS: Record<string, string> = {
  waiting: "en attente",
  running: "en cours",
  success: "réussie",
  fail: "échouée",
};

interface IProps {
  // Prévient le parent qu'un lancement/une transition a eu lieu (pour rafraîchir la liste).
  onChanged?: () => void;
}

export default function SyncLauncher({ onChanged }: IProps) {
  const notify = useNotification();
  const confirm = useImperativeDialog();

  // Périmètre actuellement lancé ou suivi (ex. "all", "pim:socoda", "supplier:FACOM") ; null = aucun.
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [supplierCode, setSupplierCode] = useState("");
  const [tracked, setTracked] = useState<string>("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Arrêt du suivi si le composant est démonté.
  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    []
  );

  function keyFor(scope: SyncScope, value?: string) {
    return value ? `${scope}:${value}` : scope;
  }

  function trackProcess(processId: number, humanLabel: string) {
    setTracked(`${humanLabel} : en attente…`);
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const process = await syncService.status(processId);
        const label = STATUS_LABELS[process.status] ?? process.status;
        setTracked(`${humanLabel} : ${label}`);

        if (process.status === "success" || process.status === "fail") {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setBusyKey(null);
          onChanged?.();
          notify(
            process.status === "success" ? "Synchronisation terminée" : "Synchronisation en échec",
            `${humanLabel} : ${label}.${process.message ? ` ${process.message}` : ""}`,
            process.status === "success" ? "success" : "error"
          );
        }
      } catch (e) {
        // Une erreur ponctuelle de lecture d'état n'interrompt pas la synchro : on retentera.
      }
    }, 3000);
  }

  async function launch(scope: SyncScope, value: string | undefined, humanLabel: string) {
    const confirmed = await confirm({
      heading: "Lancer une synchronisation",
      text: `Confirmez-vous le lancement de « ${humanLabel} » ? La mise à jour des données se fait en tâche de fond.`,
      confirmText: "Oui, lancer",
    });
    if (!confirmed) return;

    const key = keyFor(scope, value);
    setBusyKey(key);
    try {
      const result = await syncService.run(scope, value);
      notify("Synchronisation lancée", `${humanLabel} — demande enregistrée.`, "success");
      onChanged?.();

      if (result.processId) {
        trackProcess(result.processId, humanLabel);
      } else {
        // Périmètre produit : traité en direct, pas de suivi.
        setBusyKey(null);
      }
    } catch (e: any) {
      notify("Synchronisation non lancée", e.message, e.status === 409 ? "warning" : "error");
      setBusyKey(null);
    }
  }

  const launching = busyKey !== null;
  const trimmedSupplier = supplierCode.trim();

  return (
    <div className="flex flex-col gap-y-base">
      <div>
        <span className="inter-base-semibold">Lancer une synchronisation manuelle</span>
        <p className="inter-small-regular text-grey-50 mt-1">
          Met à jour les données produits depuis les PIM. Une seule synchronisation par périmètre à la
          fois.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          size="small"
          loading={busyKey === "all"}
          disabled={launching}
          onClick={() => launch("all", undefined, "Synchronisation complète")}
        >
          <RefreshIcon size={16} />
          Synchronisation complète
        </Button>
        <Button
          variant="secondary"
          size="small"
          loading={busyKey === "pim:socoda"}
          disabled={launching}
          onClick={() => launch("pim", "socoda", "PIM Socoda")}
        >
          Socoda
        </Button>
        <Button
          variant="secondary"
          size="small"
          loading={busyKey === "pim:interactiv"}
          disabled={launching}
          onClick={() => launch("pim", "interactiv", "PIM Interactiv")}
        >
          Interactiv
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2 max-w-lg">
        <InputField
          label="Par fournisseur (code)"
          placeholder="ex : FACOM"
          value={supplierCode}
          onChange={(e) => setSupplierCode(e.target.value)}
        />
        <Button
          variant="secondary"
          size="small"
          loading={busyKey === `supplier:${trimmedSupplier}`}
          disabled={launching || trimmedSupplier === ""}
          onClick={() => launch("supplier", trimmedSupplier, `Fournisseur ${trimmedSupplier}`)}
        >
          Lancer
        </Button>
      </div>

      {tracked && (
        <div className="inter-small-regular text-grey-50 border-t border-grey-20 pt-base">
          Suivi : {tracked}
        </div>
      )}
    </div>
  );
}
